import { Thread } from '@/models/Thread';
import { WidgetData, WidgetId } from '@/models/PanelWidgets';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OpenWidgetPayload {
  threadId: string;
  widgetId: string;
  widgetData: WidgetData | null;
}

export interface UIState {
  currentThread: Thread | null;
  openWidgetIds: WidgetId[];
  isDrawerOpen: boolean;
}

const initialState: UIState = {
  currentThread: null,
  openWidgetIds: [],
  isDrawerOpen: false,
};

/*************
 * reducers *
 *************/
export const setCurrentThreadReducer = (
  state: UIState,
  action: PayloadAction<Thread | null>,
) => {
  if (state.currentThread?.id === action.payload?.id) return;
  state.currentThread = action.payload;
  state.openWidgetIds = []; // clear open widgets when thread changes
};

export const openWidgetReducer = (
  state: UIState,
  action: PayloadAction<OpenWidgetPayload>,
) => {
  const widgetId = action.payload.widgetId;
  const existingWidget = state.openWidgetIds.find((id) => id === widgetId);

  if (existingWidget) {
    return;
  }

  // HACK: we only allow 1 instance of each widget type for now so clear out the widgets list before a new widget is opened
  state.openWidgetIds = [];

  state.openWidgetIds.push(widgetId);

  state.isDrawerOpen = false;
};

export const closeWidgetReducer = (state: UIState) => {
  state.openWidgetIds = [];
};

export const openDrawerReducer = (state: UIState) => {
  state.isDrawerOpen = true;
};

export const closeDrawerReducer = (state: UIState) => {
  state.isDrawerOpen = false;
};

/*************
 * selectors *
 *************/
export const getCurrentThreadSelector = (state: UIState): Thread | null =>
  state.currentThread;

export const getCurrentOpenWidgetIdsSelector = (state: UIState): WidgetId[] =>
  state.openWidgetIds;

/*************
 * slice *
 *************/
export const UIStateSlice = createSlice({
  name: 'UIState',
  initialState,
  reducers: {
    openWidget: openWidgetReducer,
    closeWidget: closeWidgetReducer,
    openDrawer: openDrawerReducer,
    closeDrawer: closeDrawerReducer,
    setCurrentThread: setCurrentThreadReducer,
  },
  selectors: {
    getCurrentThread: getCurrentThreadSelector,
    getCurrentOpenWidgetIds: getCurrentOpenWidgetIdsSelector,
  },
});

export const { getCurrentOpenWidgetIds, getCurrentThread } =
  UIStateSlice.selectors;
export const {
  closeWidget,
  openWidget,
  openDrawer,
  closeDrawer,
  setCurrentThread,
} = UIStateSlice.actions;
export const UIStateReducer = UIStateSlice.reducer;
